/* eslint-disable no-param-reassign */
import { Draft } from 'immer';
import * as Sentry from '@sentry/node';
import { LeadState } from './lead.types';
import leadSession from './leadSession';

export const REFRESH = 'REFRESH';
export const INIT = 'INIT';
export const BILLING_INFO = 'BILLING_INFO';
export const HOME_INFO = 'HOME_INFO';
export const SET_PACKAGE = 'SET_PACKAGE';
export const SET_DISCOUNT = 'SET_DISCOUNT';
export const SCHEDULE = 'SCHEDULE';
export const SET_RESIDENCE = 'SET_RESIDENCE';
export const SET_TIP = 'SET_TIP';
export const SET_APPOINTMENT = 'SET_APPOINTMENT';
export const CLEAR = 'CLEAR';

type ActionType = typeof REFRESH
  | typeof INIT
  | typeof BILLING_INFO
  | typeof HOME_INFO
  | typeof SET_PACKAGE
  | typeof SET_DISCOUNT
  | typeof SCHEDULE
  | typeof SET_RESIDENCE
  | typeof SET_TIP
  | typeof SET_APPOINTMENT
  | typeof CLEAR;

export type LeadAction = {
    type: ActionType;
    payload: Partial<LeadState>;
    // fieldName? : string;
}

const integerFields = new Set(['price', '']);

function leadReducer(draft : Draft<LeadState>, action : LeadAction) {

  if (action.payload == undefined) { return }

  switch (action.type) {
    case REFRESH:
      /* add lead id to session */
      leadSession.set(action.payload.id, action.payload.franchisee);
      Object.keys(action.payload)
        .forEach((key) => {
          if (integerFields.has(key)) {
            draft[key] = parseInt(action.payload[key], 10);
          } else {
            draft[key] = action.payload[key];
          }
        });

      return draft;

    case INIT:
    case BILLING_INFO:
      /* add lead id to session */
      leadSession.set(action.payload.id, action.payload.franchisee);

      draft.id = action.payload.id;
      draft.firstName = action.payload.firstName;
      draft.lastName = action.payload.lastName;
      draft.email = action.payload.email;
      draft.phonePrimary = action.payload.phonePrimary;
      draft.step = action.payload.step;
      draft.franchisee = action.payload.franchisee;
      draft.ipAddress = action.payload.ipAddress;
      draft.leadNotification = action.payload.leadNotification;
      draft.zipCode = action.payload.zipCode;
      draft.city = action.payload.city;
      draft.state = action.payload.state;
      draft.bedrooms = action.payload.bedrooms;
      draft.bathrooms = action.payload.bathrooms;
      draft.people = action.payload.people;
      draft.pets = action.payload.pets;
      draft.squareFootage = action.payload.squareFootage;

      Sentry.setExtra('lead', {
        id: action.payload.id,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        ipaddress: action.payload.ipAddress,
        franchisee: action.payload.franchisee,
        zipcode: action.payload.zipCode
      });

      Sentry.setTag('email', action.payload.email);
      return draft;

    case HOME_INFO:
      draft.bedrooms = action.payload.bedrooms;
      draft.bathrooms = action.payload.bathrooms;
      draft.people = action.payload.people;
      draft.pets = action.payload.pets;
      draft.squareFootage = action.payload.squareFootage;
      draft.step = action.payload.step;

      Sentry.setExtra('homeInfo', {
        bedrooms: action.payload.bedrooms,
        bathrooms: action.payload.bathrooms,
        people: action.payload.people,
        pets: action.payload.pets,
        squareFootage: action.payload.squareFootage,
      });
      return draft;

    case SET_PACKAGE:
      draft.package = action.payload.package;
      draft.price = action.payload.price;
      draft.tax = action.payload.tax;
      draft.serviceFee = action.payload.serviceFee;
      draft.totalPrice = action.payload.totalPrice;
      // draft.useAltPackage = action.payload.useAltPackage;
      draft.step = action.payload.step;
      draft.duration = action.payload.duration;
      draft.watchedVideo = action.payload.watchedVideo;

      Sentry.setExtra('packageInfo', {
        package: action.payload.package,
        price: action.payload.price,
        tax: action.payload.tax,
        totalPrice: action.payload.totalPrice,
        // useAltPackage: action.payload.useAltPackage,
        duration: action.payload.duration,
      });

      return draft;

    case SET_DISCOUNT:
      draft.watchedVideo = true;
      draft.step = action.payload.step;

      Sentry.setTag('watchedVideo', 'true');
      return draft;

    case SCHEDULE:
      // console.log("The lead schedule payload is: ", action.payload);
      draft.dateTime = action.payload.dateTime;
      draft.team = action.payload.team;
      leadSession.set(
        action.payload.id, // leadId
        action.payload.franchisee, // franchiseeId
        action.payload.dateTime, // cleaningDate
      );

      Sentry.setExtra('schedule', {
        dateTime: action.payload.dateTime,
        team: action.payload.team,
        franchisee: action.payload.franchisee,
      });
      return draft;

    case SET_RESIDENCE:
      // console.log("The lead residence payload is: ", action.payload);
      draft.addressLineOne = action.payload?.addressLineOne ?? '';
      draft.addressLineTwo = action.payload?.addressLineTwo ?? '';
      draft.city = action.payload?.city ?? '';
      draft.state = action.payload?.state ?? '';
      draft.zipCode = action.payload?.zipCode ?? '';
      draft.step = action.payload?.step ?? 0;

      return draft;

    case SET_TIP:
      draft.tip = action.payload.tip;
      draft.totalPrice = action.payload.totalPrice;

      return draft;

    case SET_APPOINTMENT:
      draft.appointment = action.payload.appointment;

      leadSession.set(
        action.payload.id,
        action.payload.franchisee,
        action.payload.dateTime,
        action.payload.appointment,
      );

      return draft;

    case CLEAR:
      leadSession.clear();

      draft.id = -1;
      draft.firstName = '';
      draft.lastName = '';
      draft.email = '';
      draft.phonePrimary = '';
      draft.franchisee = -1;

      [
        'step',
        'source',
        'referal',
        'hasBeenContacted',
        'lastContacted',
        'hasBeenEmailed',
        'ipAddress',
        'people',
        'pets',
        'bedrooms',
        'bathrooms',
        'squareFootage',
        'package',
        'price',
        'tax',
        'tip',
        'totalPrice',
        'dateTime',
        'duration',
        // 'useAltPackage',
        'team',
        'addressLineOne',
        'addressLineTwo',
        'city',
        'state',
        'zipCode',
        'customer',
        'watchedVideo',
        'comments',
        'watchedVideo',
        'appointment',
      ].forEach((key) => {
        delete draft[key];
      });

      return draft;

    default:
      return draft;
  }
}

export default leadReducer;
